import { Component, ViewChild, ElementRef, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import { LlModelsService } from '../ll-models.service';
import { LlPermissionsService } from '../ll-permissions.service';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

interface Column {
  Label: string;
  columnCode: string;
}

interface CmsRecord {
  locationName: string;
  CMSCertificationNumberCCN: string;
  OverallRating: number;
  OverallRating_vsAvg: string;
  HealthInspectionRating: number;
  HealthInspectionRating_vsAvg: string;
  QMRating: number;
  QMRating_vsAvg: string;
  LongStayQMRating: number;
  LongStayQMRating_vsAvg: string;
  ShortStayQMRating: number;
  ShortStayQMRating_vsAvg: string;
  StaffingRating: number;
  StaffingRating_vsAvg: string;
  RatingCycle1StandardSurveyHealthDate: string;
  AverageNumeric: number;
}

interface Bundle {
  showHorzTabs: boolean;
  showRemaining: boolean;
  function: string;
  records: CmsRecord[];
  parentW: number;
  parentH: number;
  remainingX: number;
  remainingY: number;
  remainingW: number;
  remainingH: number;
}

@Component({
  selector: 'll-cms-providerinfo-dg',
  templateUrl: './ll-cms-providerinfo-dg.component.html',
  styleUrls: ['./ll-cms-providerinfo-dg.component.css']
})
export class LlCmsProviderinfoDgComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mainDiv_ll_cms_providerinfo_dg') mainDiv: ElementRef<HTMLDivElement>;
  @ViewChild('horzTabs_ll_cms_providerinfo_dg') horzTabs: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();
  isLoading = false;
  error: string | null = null;

  bundle: Bundle = {
    showHorzTabs: false,
    showRemaining: false,
    function: '',
    records: [],
    parentW: 0,
    parentH: 0,
    remainingX: 0,
    remainingY: 20,
    remainingW: 0,
    remainingH: 0
  };

  private columns: Column[] = [
    { Label: 'Location', columnCode: 'locationName' },
    { Label: 'Fed Id', columnCode: 'CMSCertificationNumberCCN' },
    { Label: 'Overall', columnCode: 'OverallRating' },
    { Label: 'Health', columnCode: 'HealthInspectionRating' },
    { Label: 'Quality', columnCode: 'QMRating' },
    { Label: 'Long Stay', columnCode: 'LongStayQMRating' },
    { Label: 'Short Stay', columnCode: 'ShortStayQMRating' },
    { Label: 'Staff', columnCode: 'StaffingRating' },
    { Label: 'Last Date', columnCode: 'RatingCycle1StandardSurveyHealthDate' },
    { Label: 'Avg Numeric', columnCode: 'AverageNumeric' }
  ];

  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor(
    private llPermissionsService: LlPermissionsService,
    private llModelsService: LlModelsService
  ) { }

  ngOnInit(): void {
    // Initialize any required services or data
  }

  ngAfterViewInit(): void {
    this.setVars();
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setVars(): void {
    this.bundle.showHorzTabs = false;
    this.bundle.showRemaining = false;
    this.bundle.function = this.mainDiv?.nativeElement.parentElement.id || '';

    this.updateDimensions();
  }

  private updateDimensions(): void {
    if (this.mainDiv?.nativeElement) {
      const parent = this.mainDiv.nativeElement.parentElement.parentElement;
      this.bundle.parentW = parent.clientWidth;
      this.bundle.parentH = parent.clientHeight;
      this.bundle.remainingX = 0;
      this.bundle.remainingW = this.bundle.parentW;
      this.bundle.remainingH = this.bundle.parentH - this.bundle.remainingY;

      this.getData();
    }
  }

  private setupResizeObserver(): void {
    if (this.mainDiv?.nativeElement) {
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(100),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.updateDimensions();
        });
    }
  }

  private getData(): void {
    this.isLoading = true;
    this.error = null;
    this.llModelsService.NG_LL_model_AR_location_CMS_data_get(
      (arr: CmsRecord[]) => {
        this.bundle.records = arr;
        this.isLoading = false;
        if (this.sortColumn) {
          this.sortBy(this.sortColumn);
        }
      },
      this.bundle.function,
      //(error: any) => {
      //  this.error = 'Failed to load CMS provider data';
      //  this.bundle.records = [];
      //  this.isLoading = false;
      //}
    );
  }

  // Expose columns for template
  getColumns(): Column[] {
    return this.columns;
  }

  // Format cell values for display
  formatCell(record: CmsRecord, columnCode: string): string {
    if (!record[columnCode] && record[columnCode] !== 0) {
      return '-';
    }
    if (columnCode === 'RatingCycle1StandardSurveyHealthDate') {
      try {
        return new Date(record[columnCode]).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return record[columnCode];
      }
    }
    if (columnCode === 'AverageNumeric' && typeof record[columnCode] === 'number') {
      return record[columnCode].toFixed(2);
    }
    return record[columnCode].toString();
  }

  // Get cell background class based on _vsAvg column
  getCellBackgroundClass(record: CmsRecord, columnCode: string): string {
    const vsAvgColumn = columnCode + '_vsAvg';
    if (record[vsAvgColumn]) {
      switch (record[vsAvgColumn]) {
        case 'GT':
          return 'cell-gt';
        case 'EQ':
          return 'cell-eq';
        case 'LT':
          return 'cell-lt';
        default:
          return '';
      }
    }
    return '';
  }

  // Sort records by column
  sortBy(columnCode: string): void {
    if (this.sortColumn === columnCode) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = columnCode;
      this.sortDirection = 'asc';
    }

    this.bundle.records = [...this.bundle.records].sort((a, b) => {
      const valA = a[columnCode] ?? '';
      const valB = b[columnCode] ?? '';

      if (columnCode === 'RatingCycle1StandardSurveyHealthDate') {
        const dateA = valA ? new Date(valA).getTime() : 0;
        const dateB = valB ? new Date(valB).getTime() : 0;
        return this.sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }

      if (typeof valA === 'number' && typeof valB === 'number') {
        return this.sortDirection === 'asc' ? valA - valB : valB - valA;
      }

      const strA = valA.toString().toLowerCase();
      const strB = valB.toString().toLowerCase();
      return this.sortDirection === 'asc'
        ? strA.localeCompare(strB)
        : strB.localeCompare(strB);
    });
  }

  // Get ARIA sort state for accessibility
  getSortState(columnCode: string): string | null {
    if (this.sortColumn !== columnCode) {
      return null;
    }
    return this.sortDirection === 'asc' ? 'ascending' : 'descending';
  }
}
